import { g as gsapWithCSS } from "@theme/index";
class TrailImage {
  constructor(el) {
    this.DOM = {
      el,
      inner: el.querySelector(".cursor-trail-item__inner") || el
    };
    gsapWithCSS.set(this.DOM.el, { opacity: 0 });
    this.DOM.el.style.pointerEvents = "none";
    this.isAnimating = false;
    this.timeline = null;
  }
  // Show image at position with fade in/out
  show(x, y, zIndex) {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.DOM.el.style.zIndex = zIndex;
    const offsetX = x - 150;
    const offsetY = y - 150;
    if (this.timeline) {
      this.timeline.kill();
    }
    this.timeline = gsapWithCSS.timeline({
      onComplete: () => {
        this.isAnimating = false;
        gsapWithCSS.set(this.DOM.el, { opacity: 0, clearProps: "transform" });
        gsapWithCSS.set(this.DOM.inner, { clearProps: "transform" });
      }
    });
    this.timeline.set(this.DOM.el, {
      x: offsetX,
      y: offsetY,
      scale: 0.7,
      rotation: -15,
      filter: "blur(8px)",
      opacity: 0
    });
    this.timeline.to(this.DOM.el, {
      opacity: 1,
      scale: 1,
      rotation: 0,
      filter: "blur(0px)",
      duration: 0.3,
      ease: "power2.out"
    }, 0);
    this.timeline.to(this.DOM.el, {
      opacity: 1,
      duration: 0.2
    });
    this.timeline.to(this.DOM.el, {
      opacity: 0,
      rotation: 5,
      filter: "blur(4px)",
      duration: 0.25,
      ease: "power2.in"
    });
  }
}
class ImageTrail {
  constructor(el) {
    this.DOM = { el };
    this.trailContainer = document.querySelector(".cursor-trail-container");
    this.images = [];
    const imageElements = this.DOM.el.querySelectorAll(".cursor-trail-item");
    imageElements.forEach((img) => this.images.push(new TrailImage(img)));
    this.imagesTotal = this.images.length;
    if (this.imagesTotal === 0) {
      return;
    }
    this.imgPosition = 0;
    this.zIndexVal = 1;
    this.isMouseInside = false;
    this.isOverCnt = false;
    this.threshold = 120;
    this.mousePos = { x: 0, y: 0 };
    this.lastSpawnPos = { x: 0, y: 0 };
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.DOM.el.addEventListener("mouseenter", this.onMouseEnter);
    this.DOM.el.addEventListener("mouseleave", this.onMouseLeave);
    this.DOM.el.addEventListener("mousemove", this.onMouseMove);
  }
  onMouseEnter() {
    this.isMouseInside = true;
  }
  onMouseLeave() {
    this.isMouseInside = false;
  }
  onMouseMove(ev) {
    const target = ev.target;
    const isOverCnt = target.closest(".cnt") !== null;
    if (isOverCnt !== this.isOverCnt) {
      this.isOverCnt = isOverCnt;
      if (this.trailContainer) {
        this.trailContainer.classList.toggle("trail-hidden", isOverCnt);
      }
    }
    if (isOverCnt) {
      return;
    }
    const rect = this.DOM.el.getBoundingClientRect();
    this.mousePos = {
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top
    };
    const distance = Math.hypot(
      this.mousePos.x - this.lastSpawnPos.x,
      this.mousePos.y - this.lastSpawnPos.y
    );
    if (distance > this.threshold) {
      this.showNextImage();
      this.lastSpawnPos = { ...this.mousePos };
    }
  }
  showNextImage() {
    let availableImg = null;
    let searchCount = 0;
    while (searchCount < this.imagesTotal) {
      const img = this.images[this.imgPosition];
      if (!img.isAnimating) {
        availableImg = img;
        break;
      }
      this.imgPosition = (this.imgPosition + 1) % this.imagesTotal;
      searchCount++;
    }
    if (availableImg) {
      this.zIndexVal++;
      availableImg.show(this.mousePos.x, this.mousePos.y, this.zIndexVal);
      this.imgPosition = (this.imgPosition + 1) % this.imagesTotal;
    }
  }
  destroy() {
    this.DOM.el.removeEventListener("mouseenter", this.onMouseEnter);
    this.DOM.el.removeEventListener("mouseleave", this.onMouseLeave);
    this.DOM.el.removeEventListener("mousemove", this.onMouseMove);
  }
}
class GridRevealAnimation {
  constructor(gridItems) {
    this.gridItems = Array.from(gridItems);
    this.observer = null;
    this.init();
  }
  init() {
    this.gridItems.forEach((item) => {
      item.offsetHeight;
    });
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, idx) => {
          if (entry.isIntersecting) {
            const delay = idx * 100;
            setTimeout(() => {
              entry.target.classList.add("grid-item-revealed");
            }, delay);
          } else {
            entry.target.classList.remove("grid-item-revealed");
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px"
      }
    );
    requestAnimationFrame(() => {
      this.gridItems.forEach((item) => {
        this.observer.observe(item);
      });
    });
  }
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
class GridMouseTrail extends HTMLElement {
  constructor() {
    super();
    this.imageTrail = null;
    this.gridReveal = null;
    this.isInitialized = false;
  }
  async connectedCallback() {
    await this.init();
  }
  disconnectedCallback() {
    if (this.imageTrail) {
      this.imageTrail.destroy();
    }
    if (this.gridReveal) {
      this.gridReveal.destroy();
    }
  }
  async init() {
    if (this.isInitialized) return;
    if (!document.getElementById("trail-container-styles")) {
      const style = document.createElement("style");
      style.id = "trail-container-styles";
      style.textContent = `
        .cursor-trail-container {
          opacity: 1;
          visibility: visible;
          transition: opacity 0.2s ease, visibility 0.2s ease;
        }
        .cursor-trail-container.trail-hidden {
          opacity: 0;
          visibility: hidden;
        }
      `;
      document.head.appendChild(style);
    }
    const gridItems = this.querySelectorAll(".grid__item");
    if (gridItems.length > 0) {
      this.gridReveal = new GridRevealAnimation(gridItems);
    }
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
      this.isInitialized = true;
      return;
    }
    const trailItems = this.querySelectorAll(".cursor-trail-item");
    if (trailItems.length === 0) {
      this.isInitialized = true;
      return;
    }
    const images = this.querySelectorAll(".cursor-trail-item__inner img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );
    this.imageTrail = new ImageTrail(this);
    this.isInitialized = true;
  }
}
if (!customElements.get("grid-mouse-trail")) {
  customElements.define("grid-mouse-trail", GridMouseTrail);
}
